import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GitPullRequestArrow, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";

/* Amendment Impact chip — surfaces the pricing-engine amendment monitor on the deal page:
   "v{N} · {X} lines flagged · {Y} need re-ground" + a one-click "Run monitor / re-ground all". */
export function AmendmentImpactChip({ dealId, reload }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/bids/${dealId}/amendments`).then((r) => setData(r.data)).catch(() => {});
  }, [dealId]);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("ironclad-refresh", h);
    return () => window.removeEventListener("ironclad-refresh", h);
  }, [load]);

  if (!data) return null;
  const amendments = data.amendments || [];
  if (!amendments.length) return null;  // no amendments on this pursuit — nothing to monitor

  const mon = data.monitor || null;
  const version = mon?.version ?? 0;
  const flagged = mon?.affected_line_count ?? 0;
  const needReground = mon ? Math.max(0, flagged - (mon.regrounded_count ?? 0)) : 0;
  const blocking = !!(mon?.blocking) || (data.regrounding_blockers || []).length > 0;
  const notRun = !mon;

  const runMonitor = async () => {
    setBusy(true);
    const tid = toast.loading("Running amendment monitor — parsing, flagging & re-grounding affected lines…");
    try {
      const r = await api.post(`/bids/${dealId}/amendments/monitor`);
      const m = r.data?.monitor || {};
      const y = Math.max(0, (m.affected_line_count ?? 0) - (m.regrounded_count ?? 0));
      toast.success(
        `Amendment monitor v${m.version ?? "?"} — ${m.affected_line_count ?? 0} line(s) flagged, ` +
        (y > 0 ? `${y} still need re-ground (send blocked).` : "all affected lines re-grounded."),
        { id: tid });
      load();
      reload && reload();
      window.dispatchEvent(new CustomEvent("ironclad-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run the amendment monitor.", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const tone = notRun
    ? "bg-zinc-100 text-zinc-700 border-zinc-200"
    : blocking
      ? "bg-amber-50 text-amber-800 border-amber-300"
      : "bg-emerald-50 text-emerald-800 border-emerald-300";
  const Icon = notRun ? GitPullRequestArrow : blocking ? ShieldAlert : ShieldCheck;

  return (
    <div className="inline-flex items-center gap-2" data-testid="amendment-impact-chip">
      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${tone}`}
            title={notRun
              ? `${amendments.length} amendment(s) detected — run the monitor to flag & re-ground affected cost lines`
              : `${flagged} cost line(s) affected by material amendments; ${needReground} not yet re-grounded since the amendment`}>
        <Icon className="h-3.5 w-3.5" />
        {notRun
          ? `${amendments.length} amendment${amendments.length > 1 ? "s" : ""} · not yet monitored`
          : `v${version} · ${flagged} line${flagged === 1 ? "" : "s"} flagged · ${needReground} need re-ground`}
      </span>
      <button
        onClick={runMonitor}
        disabled={busy}
        data-testid="run-amendment-monitor-btn"
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-zinc-900 text-white px-2.5 py-1 rounded-full hover:bg-zinc-800 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitPullRequestArrow className="h-3.5 w-3.5" />}
        Run monitor / re-ground all
      </button>
    </div>
  );
}
